import { nanoid } from 'nanoid'
import type { StoreApi } from 'zustand'
import { QueryClient } from '@tanstack/react-query'
import type { Socket } from 'socket.io-client'
import type {
  AgentFrontendConfig,
  AgentToolDefinition,
  AgentToolHandlerContext,
  ToolRendererComponent,
  ValidatedAgentFrontendConfig,
  AgentTransportConfig,
  PageContextInput,
  ChatMetadataInput
} from '../config/types'
import type { AgentStore } from '../state/useAgentStore'
import type { AgentTransport } from './transports/AgentTransport'
import type { AgentAttachment } from '@multiplayer-app/ai-agent-types'

import { ProxyTransport } from './transports/ProxyTransport'
import { DirectTransport } from './transports/DirectTransport'
import { ToolRegistry, type ToolInvokeOptions } from './ToolRegistry'
import { AgentToolCallStatus, type AgentToolCall } from './types'
import { frontendConfigSchema } from '../config/types'
import { defaultTranslations } from '../config/translations'
import { FormProposalToolRenderer, ImageToolRenderer } from '../components/ToolRenderers'
import { createPageContextAttachment } from '../utils/contextAttachments'

export class AgentRuntime {
  readonly config: ValidatedAgentFrontendConfig
  transport: AgentTransport
  readonly tools: ToolRegistry
  readonly queryClient: QueryClient
  /**
   * Per-runtime event bus for host integrations (e.g. form apply requests).
   * Host apps can subscribe via `runtime.events.addEventListener(...)`.
   */
  readonly events: EventTarget
  private socketClient: Socket | null = null
  private readonly toolRendererMap = new Map<string, ToolRendererComponent>()
  private storeRef?: StoreApi<AgentStore>
  private readonly pageContextProviders = new Set<(input: PageContextInput) => Record<string, unknown> | undefined>()
  private readonly chatMetadataProviders = new Set<
    (input: ChatMetadataInput) => Record<string, unknown> | undefined
  >()

  constructor(private rawConfig: AgentFrontendConfig) {
    this.events = new EventTarget()
    const parsed = frontendConfigSchema.parse(rawConfig)
    this.config = {
      ...parsed,
      // Preserve tool handlers which are functions and can't be validated by Zod
      tools: rawConfig.tools,
      // Preserve optional pageContext hooks which are functions and can't be validated by Zod
      pageContext: rawConfig.pageContext
        ? { ...(parsed as any).pageContext, ...rawConfig.pageContext }
        : (parsed as any).pageContext,
      // getMetadata is a function stripped by Zod; re-attach from rawConfig.
      chatMetadata: rawConfig.chatMetadata
        ? { ...(parsed as any).chatMetadata, ...rawConfig.chatMetadata }
        : (parsed as any).chatMetadata,
      // Merge user translations with defaults
      translations: rawConfig.translations
        ? { ...defaultTranslations, ...rawConfig.translations }
        : defaultTranslations,
      // Preserve onError callback from transport config (stripped by Zod since it's a function)
      transport: rawConfig.transport.onError
        ? ({ ...parsed.transport, onError: rawConfig.transport.onError } as AgentTransportConfig)
        : (parsed.transport as AgentTransportConfig),
      // Preserve ReactNode values which are stripped by Zod
      toolRenderers: rawConfig.toolRenderers,
      planExtractor: rawConfig.planExtractor,
      contextAttachmentIcons: rawConfig.contextAttachmentIcons,
      composerAttachmentActions: rawConfig.composerAttachmentActions
    }

    this.transport = this.createTransport(this.config.transport)

    this.tools = new ToolRegistry(this.config.tools)
    this.tools.bindInvoke(this.invokeTool.bind(this))

    // Register tools/models/translations with DirectTransport (if applicable)
    this.configureTransportAfterCreate(this.transport)
    Object.entries(rawConfig.toolRenderers ?? {}).forEach(([toolName, renderer]) => {
      if (renderer) this.toolRendererMap.set(toolName, renderer)
    })

    // Built-in default renderer for form fill proposals.
    // Do not override host-provided renderers.
    if (!this.toolRendererMap.has('propose_form_values')) {
      this.toolRendererMap.set('propose_form_values', FormProposalToolRenderer)
    }

    // Built-in default renderer for generated images.
    if (!this.toolRendererMap.has('image_generation')) {
      this.toolRendererMap.set('image_generation', ImageToolRenderer)
    }

    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false
        }
      }
    })
  }

  private createTransport(transportConfig: AgentTransportConfig): AgentTransport {
    return transportConfig.mode === 'proxy' ? new ProxyTransport(transportConfig) : new DirectTransport(transportConfig)
  }

  private configureTransportAfterCreate(transport: AgentTransport) {
    // Register tools with DirectTransport so it can send them to the LLM API
    if (this.config.transport.mode === 'direct' && transport instanceof DirectTransport) {
      transport.setToolRegistry(this.tools)
      transport.setContextKeys(
        this.config.contextKeys.map((ck) => ({
          key: ck.key,
          tools: ck.tools
        }))
      )
      if (this.config.models) {
        transport.setModels(this.config.models)
      }
      transport.setUntitledChatText(this.config.translations.untitledChat)
    }
  }

  /**
   * Update transport configuration at runtime.
   *
   * This is intentionally explicit because transports cache config internally and the UI
   * needs a rerender/reconnect to pick up changes (e.g. Socket.IO auth headers).
   */
  updateTransportConfig(nextTransport: AgentTransportConfig) {
    // Validate the config shape via Zod (same path as constructor).
    const parsed = frontendConfigSchema.parse({ ...this.rawConfig, transport: nextTransport })

    const prevTransportConfig = this.config.transport
    const prevTransport = this.transport

    // Update raw + validated config in-place.
    this.rawConfig = { ...this.rawConfig, transport: nextTransport }
    // Preserve onError callback (stripped by Zod since it's a function)
    this.config.transport = nextTransport.onError
      ? ({ ...parsed.transport, onError: nextTransport.onError } as AgentTransportConfig)
      : (parsed.transport as AgentTransportConfig)

    const nextMode = this.config.transport.mode

    // If the mode changes, swap transport instances (semantics differ).
    if (prevTransportConfig.mode !== nextMode) {
      prevTransport.cancelStream?.()
      this.transport = this.createTransport(this.config.transport)
      this.configureTransportAfterCreate(this.transport)
      this.events.dispatchEvent(
        new CustomEvent('runtime:transport-config-changed', {
          detail: { previous: prevTransportConfig, next: this.config.transport }
        })
      )
      return
    }

    // Same mode: update in-place when supported to preserve internal caches (direct chat storage, proxy stream map).
    if (nextMode === 'proxy' && prevTransport instanceof ProxyTransport) {
      prevTransport.updateConfig(this.config.transport)
    } else if (nextMode === 'direct' && prevTransport instanceof DirectTransport) {
      prevTransport.updateConfig(this.config.transport)
      this.configureTransportAfterCreate(prevTransport)
    } else {
      // Fallback: replace transport if class doesn't match.
      prevTransport.cancelStream?.()
      this.transport = this.createTransport(this.config.transport)
      this.configureTransportAfterCreate(this.transport)
    }

    this.events.dispatchEvent(
      new CustomEvent('runtime:transport-config-changed', {
        detail: { previous: prevTransportConfig, next: this.config.transport }
      })
    )
  }

  /**
   * Replace the full transport headers map (pass undefined to clear).
   */
  setTransportHeaders(headers?: Record<string, string>) {
    const current = this.config.transport
    this.updateTransportConfig({ ...current, headers } as AgentTransportConfig)
  }

  /**
   * Merge headers into the current transport headers (overwrites existing keys).
   */
  mergeTransportHeaders(headers: Record<string, string>) {
    const current = this.config.transport
    this.updateTransportConfig({
      ...current,
      headers: { ...(current.headers ?? {}), ...headers }
    } as AgentTransportConfig)
  }

  /**
   * Remove specific header keys from the current transport headers.
   */
  removeTransportHeaders(keys: string[]) {
    const current = this.config.transport
    if (!current.headers || keys.length === 0) return
    const next = { ...current.headers }
    for (const key of keys) delete next[key]
    this.updateTransportConfig({
      ...current,
      headers: Object.keys(next).length ? next : undefined
    } as AgentTransportConfig)
  }

  get defaultContextKey() {
    return this.config.defaultContextKey ?? this.config.contextKeys[0]?.key
  }

  get store(): StoreApi<AgentStore> {
    if (!this.storeRef) {
      throw new Error('AgentRuntime store has not been attached yet')
    }
    return this.storeRef
  }

  attachStore(store: StoreApi<AgentStore>) {
    this.storeRef = store
  }

  /**
   * Live Socket.IO client when proxy transport is used and `SocketProvider` has created a client.
   * Host apps can attach listeners with `runtime.socket?.on(...)`.
   * Listen for `runtime:socket-changed` on `runtime.events` if you need to react when the instance is replaced.
   */
  get socket(): Socket | null {
    return this.socketClient
  }

  /**
   * @internal Used by `SocketProvider` to publish the active client (or clear it on teardown / non-proxy mode).
   */
  setSocketClient(next: Socket | null) {
    if (this.socketClient === next) return
    this.socketClient = next
    this.events.dispatchEvent(new CustomEvent('runtime:socket-changed', { detail: { socket: next } }))
  }

  registerTool(tool: AgentToolDefinition) {
    this.tools.register(tool)
  }

  registerToolRenderer(toolName: string, renderer: ToolRendererComponent) {
    this.toolRendererMap.set(toolName, renderer)
  }

  /**
   * Register a page-level provider for extra page context data.
   * Intended to be called by route/page components (via a hook).
   *
   * The returned cleanup function MUST be called (on unmount) to avoid leaks.
   */
  registerPageContextProvider(provider: (input: PageContextInput) => Record<string, unknown> | undefined): () => void {
    this.pageContextProviders.add(provider)
    return () => {
      this.pageContextProviders.delete(provider)
    }
  }

  /**
   * Register a provider contributing chat-level metadata, merged into a new
   * chat's `metadata` at creation time. Intended to be called by route/page
   * components (via the `useChatMetadata` hook).
   *
   * The returned cleanup function MUST be called (on unmount) to avoid leaks.
   */
  registerChatMetadataProvider(
    provider: (input: ChatMetadataInput) => Record<string, unknown> | undefined
  ): () => void {
    this.chatMetadataProviders.add(provider)
    return () => {
      this.chatMetadataProviders.delete(provider)
    }
  }

  /**
   * Resolve the metadata to persist on a newly created chat by merging the
   * config-level `chatMetadata.getMetadata` hook with all registered providers.
   * Returns undefined when nothing contributed (so callers can omit the field).
   */
  getChatMetadata(input: ChatMetadataInput): Record<string, unknown> | undefined {
    const merged: Record<string, unknown> = {}
    let hasAny = false

    const collect = (data: Record<string, unknown> | undefined) => {
      if (data && typeof data === 'object') {
        Object.assign(merged, data)
        hasAny = true
      }
    }

    // 1) Global config hook.
    const fn = this.config.chatMetadata?.getMetadata
    if (fn) {
      try {
        collect(fn(input))
      } catch {
        // ignore
      }
    }

    // 2) Page-level providers (registered by route/page components).
    for (const provider of this.chatMetadataProviders) {
      try {
        collect(provider(input))
      } catch {
        // ignore
      }
    }

    return hasAny ? merged : undefined
  }

  getPageContextInput(): PageContextInput {
    if (typeof window === 'undefined') return {}
    const url = window.location?.href
    let route: string | undefined
    if (url) {
      try {
        const u = new URL(url)
        route = `${u.pathname}${u.search}${u.hash}`
      } catch {}
    }
    const title = typeof document !== 'undefined' ? document.title : undefined
    return { url, route, title }
  }

  getPageContextData(input?: PageContextInput): Record<string, unknown> | undefined {
    const ctx = input ?? this.getPageContextInput()
    const merged: Record<string, unknown> = {}
    let hasAny = false

    // 1) Global config hook.
    const fn = this.config.pageContext?.getData
    if (fn) {
      try {
        const data = fn(ctx)
        if (data && typeof data === 'object') {
          Object.assign(merged, data)
          hasAny = true
        }
      } catch {
        // ignore
      }
    }

    // 2) Page-level providers (registered by route/page components).
    for (const provider of this.pageContextProviders) {
      try {
        const data = provider(ctx)
        if (data && typeof data === 'object') {
          Object.assign(merged, data)
          hasAny = true
        }
      } catch {
        // ignore
      }
    }

    return hasAny ? merged : undefined
  }

  buildPageContextAttachment(): AgentAttachment | undefined {
    const cfg = this.config.pageContext
    const extra = this.getPageContextData()
    return createPageContextAttachment({
      ...(cfg?.includeUserAgent !== undefined ? { includeUserAgent: cfg.includeUserAgent } : {}),
      ...(cfg?.includeReferrer !== undefined ? { includeReferrer: cfg.includeReferrer } : {}),
      ...(cfg?.includeViewport !== undefined ? { includeViewport: cfg.includeViewport } : {}),
      ...(cfg?.includeTitle !== undefined ? { includeTitle: cfg.includeTitle } : {}),
      ...(cfg?.name ? { name: cfg.name } : {}),
      ...(extra ? { extra } : {})
    })
  }

  getToolRenderer(call: AgentToolCall): ToolRendererComponent | undefined {
    return this.toolRendererMap.get(call.name)
  }

  private ensureStore(action: string) {
    if (!this.storeRef) {
      throw new Error(`Cannot ${action} before AgentRuntime is mounted`)
    }
    return this.storeRef
  }

  private resolveChatId(options?: ToolInvokeOptions) {
    const store = this.ensureStore('invoke tools')
    const state = store.getState()
    const chatId = options?.chatId ?? state.activeChatId
    if (!chatId || !state.chats[chatId]) {
      throw new Error('Tool invocation requires an active chat. Select or create a chat first.')
    }
    return chatId
  }

  private buildToolContext(chatId: string): AgentToolHandlerContext {
    const store = this.ensureStore('create tool context')
    return {
      chatId,
      agentId: this.config.appId,
      workspaceId: this.config.workspaceId,
      appendSystemMessage: (message: string) => {
        store.getState().appendSystemMessage(chatId, message)
      }
    }
  }

  private writeToolCall(chatId: string, call: AgentToolCall) {
    const store = this.ensureStore('write tool call')
    store.getState().upsertToolCall(chatId, { ...call })
  }

  private async invokeTool(
    name: string,
    input: Record<string, unknown> = {},
    options?: ToolInvokeOptions
  ): Promise<AgentToolCall> {
    const chatId = this.resolveChatId(options)
    const preCall: AgentToolCall = {
      id: nanoid(),
      name,
      input,
      status: AgentToolCallStatus.Running
    }
    this.writeToolCall(chatId, preCall)
    const context = this.buildToolContext(chatId)
    try {
      const result = await this.tools.execute(name, input, context, preCall)
      this.writeToolCall(chatId, result)
      return result
    } catch (error) {
      const failedCall: AgentToolCall = {
        ...preCall,
        status: AgentToolCallStatus.Failed,
        error: error instanceof Error ? error.message : 'Unknown tool error'
      }
      this.writeToolCall(chatId, failedCall)
      throw error
    }
  }
}
